import { dayjs, RootState, usePersistantState } from '@umanforge/common';
import ChartLoader from 'components/charts/chartGraph';

import DateSelector from 'components/inputs/dateSelector';
import Page from 'components/menu/page.component';
import StatisticFooter from 'components/menu/chart/statisticFooter.component';
import StatisticRow from 'components/menu/chart/statisticRow.component';
import { KEY } from 'constants/local_storage_key.constant';
import { ChartData, ChartFilter } from 'interfaces/chart.interface';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ChartService } from 'services/chart.service';
import StatisticTab from 'components/menu/chart/statisticTab.component';
import { PlanningService } from 'services/planning.service';
import { Planning } from 'interfaces/planning.interface';
import { GlobalService } from 'services/global.service';
import { SettingService } from 'services/setting.service';
import FilterSelector from 'features/filter_component/components/filterSelector.component';
import ExtractionButton from 'features/extraction/components/extraction_button.component';
import ExtractionModal from 'features/extraction/components/extraction_modal.component';
import { Tooltip } from 'react-tooltip';

function Chart() {
  const { t, i18n } = useTranslation();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();

  const [ currentDay, setCurrentDay ] = useState<dayjs.Dayjs>( dayjs() );
  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ showWeenkEnd, setShowWeekEnd ] = useState( true );
  const [ planning, setPlanning ] = useState<Planning>(  );

  const [ data, setData ] = useState<ChartData[]>( [] );
  const [ daysName, setDaysName ] = useState<string[]>( [] );
  const [ totalAllocated, setTotalAllocated ] = useState( 0 );
  const [ totalAchieved, setTotalAchieved ] = useState( 0 );
  const [ totalDifference, setTotalDifference ] = useState( 0 );

  const [ totalTask, setTotalTask ] = useState( 0 );
  const [ totalCompleted, setTotalCompleted ] = useState( 0 );
  const [ totalCanceled, setTotalCanceled ] = useState( 0 );

  const [ filters, setFilters ] = useState<ChartFilter>( {} );
  const [ filtersOpen, setFiltersOpen ] = useState<boolean>( false );

  const [ extractionModal, setExtractionModal ] = useState<boolean>( false );

  const dates = useMemo( () => {
    return {
      dateStart: currentDay.startOf( 'day' ).toISOString(),
      dateEnd: currentDay.add( showWeenkEnd ? 6 : 4, 'day' ).endOf( 'day' ).toISOString()
    };
  }, [ currentDay, showWeenkEnd ] );

  const colors = useMemo( () => {
    return {
      alocatedTime: '#5470c6',
      achievedTime: '#91cc75',
      differenceTime: '#fac858',
      canceledTask: '#ee6667',
    };
  }, [ ] );

  useEffect( () => {
    if ( data && data.length >= 6 ) {
      const totalAchievedValue = data[ 0 ].data.reduce( ( a, b ) => {return a + b;}, 0 );
      const totalAllocatedValue = data[ 1 ].data.reduce( ( a, b ) => {return a + b;}, 0 );
      setTotalAchieved( totalAchievedValue );
      setTotalAllocated( totalAllocatedValue );
      setTotalDifference( totalAchievedValue - totalAllocatedValue );

      setTotalTask( data[ 3 ].data.reduce( ( a, b ) => {return a + b;}, 0 ) );
      setTotalCompleted( data[ 4 ].data.reduce( ( a, b ) => {return a + b;}, 0 ) );
      setTotalCanceled( data[ 5 ].data.reduce( ( a, b ) => {return a + b;}, 0 ) );
    }
    else {
      setTotalAllocated( 0 );
      setTotalAchieved( 0 );
      setTotalDifference( 0 );
      setTotalTask( 0 );
      setTotalCompleted( 0 );
      setTotalCanceled( 0 );
    }
  }, [ data ] );

  const refreshPlanning = async ( sectorId: string | undefined ) => {
    if ( sectorId ) {
      setPlanning( await PlanningService.getPlanningBySectorId( sectorId, token, dispatch ) );
      const settings = await SettingService.getSectorSettingsBySector( sectorId, token, dispatch );
      if ( settings ) {
        setShowWeekEnd( settings.showWeekend?.value == 1 );
      }
    }
  };

  const refreshData = async ( ) => {
    if ( planning ) {
      setData( await ChartService.getChartsByDatesAndPlanningId(
        planning.id,
        {
          ...dates,
          timezone: '',
          filters: filters
        }, token, dispatch ) );
    }
  };

  const onSectorsChange = ( sectorId: string | undefined ) => {
    if ( !isFirstRender ) {
      setSelectedSectorId( sectorId );
    }
  };

  //--------------- useEffect ---------------\\
  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender && planning ) {
      refreshData();
    }
  }, [ planning, dates, filters ] );

  useEffect( () => {
    if ( !isFirstRender && selectedSectorId ) {
      refreshPlanning( selectedSectorId );

      // Générer les jours de la semaine traduits avec dayjs
      const currentLocale = dayjs.locale();
      if ( i18n.language ) {
        dayjs.locale( i18n.language );
      }
      const daysOfWeek = [];
      const startOfWeek = dayjs().startOf( 'week' );
      for ( let i = 0; i < ( showWeenkEnd ? 7 : 5 ); i++ ) {
        daysOfWeek.push( startOfWeek.add( i, 'day' ).format( 'ddd' ) );
      }
      dayjs.locale( currentLocale );

      setDaysName( daysOfWeek );
    }
  }, [ selectedSectorId, isFirstRender, showWeenkEnd ] );


  return (
    <Page
      isAdminRequired={ true }
      onSectorChange={onSectorsChange}
      headerNavigateBarChildren={
        <div className='grid grid-flow-col grid-cols-3 self-justify-end w-full'>
          <div className='col-span-2'>
            <DateSelector
              setCurrentDay={ setCurrentDay }
              currentDay={ currentDay }
              isWeek={ true }
            />
          </div>
          <ExtractionButton
            className='z-20'
            onClick={ () => { setExtractionModal( !extractionModal ); } }
          />
        </div>
      }
    >
      <>
        <div className="p-0 mt-1 -mr-4 min-h-[80vh] flex w-full"> {/* Changed height to min-h to allow dynamic adjustment */}
          { selectedSectorId &&
            <FilterSelector
              className='mt-6'
              sectorId={ selectedSectorId }
              onChange={ setFilters }
              onOpen={ setFiltersOpen }
              isOpen={ filtersOpen }
            />
          }
          <div className={ `grid grid-cols-1 gap-6 mt-6 w-full ${ filtersOpen ? '2xl:grid-cols-2' : 'lg:grid-cols-2 2xl:grid-cols-3' }` }>
            <StatisticTab>
              <StatisticRow
                title={ t( 'translation:stats.somme_of_achieved_time' ) }
                value={ GlobalService.decimalToTimeColapsed( totalAchieved ) }
                className='text-[#91cc75]'
              />
              <StatisticRow
                title={ t( 'translation:stats.somme_of_allocated_time' ) }
                value={ GlobalService.decimalToTimeColapsed( totalAllocated )  }
                className=' border-t border-app-color-light text-[#5470c6]'
              />
              <StatisticFooter title={ t( 'translation:stats.difference_achieved_minus_allocated' ) }>
                <div>
                  <StatisticRow
                    title={''}
                    value={ GlobalService.decimalToTimeColapsed( totalDifference )  }
                    className='mt-5 bg-transparent text-[#fac858]'
                  />
                </div>
              </StatisticFooter>
            </StatisticTab>

            <div className="bg-white border shadow-md w-full h-[32rem] p-5 rounded-xl flex flex-col justify-center items-center"> {/* Enforced consistent height */}
              { ( data && data.length > 0 ) ? (
                <ChartLoader
                  chartTitle={'test'}
                  option={{
                    ...ChartService.getBarOption( t( 'translation:time_diagram' ) ),
                    xAxis: {
                      type: 'category',
                      data: daysName
                    },
                    tooltip: {
                      trigger: 'axis',
                      axisPointer: {
                        type: 'shadow',
                        shadowStyle: {
                          color: 'rgba(150, 150, 150, 0.1)'
                        }
                      },
                      textStyle: {
                        color: '#ffffff',
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: 'Arial, sans-serif'
                      },
                      backgroundColor: 'rgba(30, 30, 30, 0.9)',
                      borderColor: '#444',
                      borderWidth: 1,
                      borderRadius: 8,
                      padding: [ 12, 18 ],
                      formatter: function( params ) {
                        if ( Array.isArray( params ) ) {
                          return (
                            '<div style=\'text-align: left;\'>' +
                            params.map( param => {
                              let value = param.value + '';
                              const numberValue = parseFloat( value );
                              if ( numberValue ) {
                                value = numberValue.toFixed( 1 );
                              }
                              return (
                                '<div style=\'margin-bottom: 4px;\'>' +
                                '<span style=\'color: ' + param.color + '; font-weight: bold;\'>' + param.seriesName + '</span>: ' +
                                '<span>' + value + '</span>' +
                                '</div>'
                              );
                            } ).join( '' ) +
                            '</div>'
                          );
                        }
                        return `<div style='text-align: center;'>${t( 'common:no_data_available' )}</div>`;
                      }
                    },
                    color: [ colors.alocatedTime, colors.achievedTime, colors.canceledTask, colors.differenceTime ],
                    series: [
                      {
                        data: data[ 1 ].data,
                        type: 'bar',
                        stack: 'one',
                        name: t( 'translation:stats.allocated_time' )
                      },
                      {
                        data: data[ 0 ].data,
                        type: 'bar',
                        stack: 'two',
                        name: t( 'translation:stats.achieved_time' )
                      },
                      {
                        data: data[ 6 ].data,
                        type: 'bar',
                        stack: 'two',
                        name: t( 'translation:stats.canceled_time' )
                      },
                      {
                        data: data[ 0 ].data.map( ( achieved, index ) => {return achieved - data[ 1 ].data[ index ];} ),
                        type: 'line',
                        name: t( 'translation:stats.achieved_minus_allocated' ),
                        symbol: 'circle',
                        symbolSize: 10,
                        showSymbol: true
                      }
                    ]
                  }}
                  update={ data.length + ( filtersOpen ? 1 : 2 ) }
                />
              ) :
                <p>{t( 'common:no_data' )}</p>
              }
            </div>

            <div className="bg-white border shadow-md w-full h-[32rem] p-2 pt-5 rounded-xl flex flex-col justify-center items-center"> {/* Enforced consistent height */}
              {data && data.length > 0 && (
                <>
                  <div className='flex justify-end w-full h-fit -mb-6 z-10'>
                    <span
                      id={'distribution-info-tooltip'}
                      className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200/60 w-6 h-6 text-gray-700 text-sm font-bold'}
                    > i </span>
                    <Tooltip
                      anchorSelect={'#distribution-info-tooltip'}
                      className={'z-[1000] max-w-[400px] text-wrap'}
                      place="top"
                      positionStrategy='fixed'
                    >
                      <p className="pt-1">{t( 'translation:distribution_is_nb_allowed_tasks_compare_to_nb_achieve_tasks' )}</p>
                    </Tooltip>
                  </div>

                  <ChartLoader
                    chartTitle={t( 'translation:tasks_distribution' )}
                    option={{
                      ...ChartService.getPieOption( t( 'translation:tasks_distribution' ) ),
                      tooltip: {
                        trigger: 'item',
                        textStyle: {
                          align: 'right'
                        }
                      },
                      color: [ colors.alocatedTime, colors.achievedTime, colors.canceledTask ],
                      series: [
                        {
                          name: '',
                          type: 'pie',
                          radius: '60%',
                          data: [
                            { value: ( totalTask - totalCompleted - totalCanceled ), name: t( 'translation:stats.allocated_task' ) },
                            { value: ( totalCompleted ), name: t( 'translation:stats.completed_task' ) },
                            { value: ( totalCanceled ), name: t( 'translation:stats.canceled_task' ) },
                          ],
                          emphasis: {
                            itemStyle: {
                              shadowBlur: 10,
                              shadowOffsetX: 0,
                              shadowColor: 'rgba(0, 0, 0, 0.5)',
                              borderColor: '#fff',
                              borderWidth: 1
                            }
                          },
                          label: {
                            show: true,
                            formatter: function( params ) {
                              return `${params.percent?.toFixed( 1 )}%`;
                            },
                            fontSize: 12
                          }
                        }
                      ]
                    }}
                    update={ filtersOpen }
                  />
                </>
              )}
            </div>
          </div>
        </div>

        { extractionModal && selectedSectorId &&
          <ExtractionModal
            planningId={ planning?.id ?? '' }
            sectorId={ selectedSectorId }
            onClose={ () => { setExtractionModal( false ); } }
            dates={ dates }
            defaultFilters={ filters }
          />
        }
      </>
    </Page>
  );
}

export default Chart;